import L from 'leaflet';
import { api, ApiError } from '../api/client';
import type { CheckinItem } from '../api/types';
import { BASEMAP_TILE_OPTIONS, basemapTileUrl } from '../lib/basemap';
import { batteryColor } from '../lib/format';
import { escapeHtml, highlightJson } from '../lib/html';
import { humanDuration, relativeTime } from '../lib/time';
import { navigate } from '../router';

/** Resolve a CSS custom property to a concrete color Leaflet can use. */
function cssVar(name: string, fallback: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

const HOURS_OPTIONS = [
  { hours: 6, label: '6h' },
  { hours: 24, label: '24h' },
  { hours: 72, label: '72h' },
  { hours: 168, label: '7d' },
];
const DEFAULT_HOURS = 24;
/** Backend cap (MAX_TRAIL_HOURS in lambda_function.py); custom ranges can't
 * reach further back than this, so the date pickers are bounded to match. */
const MAX_HISTORY_HOURS = 168;

/** connected_ssid (v2, ConnectivityManager) wins when present; falls back to
 * the always-sent wifi.ssid (v1) when actually connected. */
function connectedSsid(item: CheckinItem): string | null {
  if (item.connected_ssid) return item.connected_ssid;
  if (item.wifi?.connected) return item.wifi.ssid;
  return null;
}

function toDateInputValue(d: Date): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function formatRangeLabel(from: string, to: string): string {
  const fmt = (s: string): string =>
    new Date(`${s}T00:00:00`).toLocaleDateString(undefined, { year: '2-digit', month: 'numeric', day: 'numeric' });
  return `${fmt(from)} – ${fmt(to)}`;
}

export function mount(root: HTMLElement, deviceId: string): () => void {
  root.innerHTML = `
    <div class="log-page">
      <div class="log-page-inner">
        <button type="button" class="log-back-btn" id="checkins-back">&larr; Back to People</button>
        <div class="log-page-head">
          <div>
            <h1 id="checkins-title">Check-in log</h1>
            <p class="subtitle" id="checkins-subtitle"></p>
          </div>
        </div>
        <div class="log-filterbar">
          <div class="log-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round">
              <circle cx="11" cy="11" r="7"></circle>
              <line x1="21" y1="21" x2="16.2" y2="16.2"></line>
            </svg>
            <input id="checkins-search" type="text" placeholder="Filter by location, Wi-Fi, battery&hellip;" />
            <button type="button" class="search-clear" id="checkins-search-clear" aria-label="Clear filter" hidden>&#10005;</button>
          </div>
          <div class="log-range">
            <div class="segmented" id="checkins-hours"></div>
            <div class="range-popover" id="checkins-popover" hidden>
              <div class="range-popover-title">Custom date range</div>
              <label for="checkins-from">From</label>
              <input type="date" id="checkins-from" />
              <label for="checkins-to">To</label>
              <input type="date" id="checkins-to" />
              <div class="range-popover-hint">History is kept for the last 7 days.</div>
              <div class="range-popover-actions">
                <button type="button" id="checkins-range-apply">Apply</button>
                <button type="button" id="checkins-range-clear">Clear</button>
              </div>
            </div>
          </div>
        </div>
        <div id="checkins-error" class="admin-error" hidden></div>
        <div id="checkins-body">Loading&hellip;</div>
      </div>
    </div>
  `;

  const backBtn = root.querySelector<HTMLButtonElement>('#checkins-back')!;
  const titleEl = root.querySelector<HTMLHeadingElement>('#checkins-title')!;
  const subtitleEl = root.querySelector<HTMLParagraphElement>('#checkins-subtitle')!;
  const hoursEl = root.querySelector<HTMLDivElement>('#checkins-hours')!;
  const errorBox = root.querySelector<HTMLDivElement>('#checkins-error')!;
  const bodyEl = root.querySelector<HTMLDivElement>('#checkins-body')!;
  const searchInput = root.querySelector<HTMLInputElement>('#checkins-search')!;
  const searchClear = root.querySelector<HTMLButtonElement>('#checkins-search-clear')!;
  const popoverEl = root.querySelector<HTMLDivElement>('#checkins-popover')!;
  const fromInput = root.querySelector<HTMLInputElement>('#checkins-from')!;
  const toInput = root.querySelector<HTMLInputElement>('#checkins-to')!;
  const applyBtn = root.querySelector<HTMLButtonElement>('#checkins-range-apply')!;
  const rangeClearBtn = root.querySelector<HTMLButtonElement>('#checkins-range-clear')!;

  let hours = DEFAULT_HOURS;
  let custom: { from: string; to: string } | null = null;
  let query = '';
  let openId: string | null = null;
  let allRows: CheckinItem[] = [];
  let disposed = false;
  let searchTimer: number | undefined;
  let loadSeq = 0;

  // At most one drawer (and therefore one mini-map) is open at a time; keep a
  // handle so it can be torn down before the drawer node is discarded — a
  // re-render, a close, or page disposal — otherwise Leaflet leaks the map
  // instance and its listeners.
  let activeMiniMap: L.Map | null = null;
  let activeThemeHandler: ((e: Event) => void) | null = null;
  function destroyMiniMap(): void {
    if (activeThemeHandler) {
      window.removeEventListener('ft-theme-change', activeThemeHandler);
      activeThemeHandler = null;
    }
    if (activeMiniMap) {
      try {
        activeMiniMap.remove();
      } catch {
        /* container already gone */
      }
      activeMiniMap = null;
    }
  }

  /** Build the check-in's location map inside an already-inserted drawer. */
  function mountMiniMap(drawer: HTMLElement, item: CheckinItem): void {
    const el = drawer.querySelector<HTMLElement>('.log-map');
    if (!el) return;
    try {
      const theme = document.documentElement.getAttribute('data-theme') ?? 'dark';
      const map = L.map(el, {
        zoomControl: false,
        attributionControl: false,
        scrollWheelZoom: false,
      }).setView([item.lat, item.lng], 16);
      let tiles = L.tileLayer(basemapTileUrl(theme), { ...BASEMAP_TILE_OPTIONS }).addTo(map);
      L.control.zoom({ position: 'bottomright' }).addTo(map);

      const pointColor = item.imprecise ? cssVar('--stale', '#e6a23c') : cssVar('--accent', '#4f9cf0');

      // Accuracy halo — for imprecise fixes this is the whole point: it shows
      // how loose the reading is rather than implying a false pinpoint.
      let accuracyCircle: L.Circle | null = null;
      if (typeof item.accuracy === 'number' && item.accuracy > 0) {
        accuracyCircle = L.circle([item.lat, item.lng], {
          radius: item.accuracy,
          color: pointColor,
          weight: 1,
          fillColor: pointColor,
          fillOpacity: 0.12,
        }).addTo(map);
      }
      L.circleMarker([item.lat, item.lng], {
        radius: 6,
        color: '#fff',
        weight: 2,
        fillColor: pointColor,
        fillOpacity: 1,
      }).addTo(map);

      // Frame the halo so a loose reading zooms out to show its spread.
      if (accuracyCircle) {
        map.fitBounds(accuracyCircle.getBounds(), { padding: [24, 24], maxZoom: 16 });
      }

      const onTheme = (e: Event): void => {
        map.removeLayer(tiles);
        tiles = L.tileLayer(basemapTileUrl((e as CustomEvent<string>).detail), { ...BASEMAP_TILE_OPTIONS }).addTo(
          map,
        );
      };
      window.addEventListener('ft-theme-change', onTheme);

      activeMiniMap = map;
      activeThemeHandler = onTheme;
    } catch {
      // If Leaflet can't initialize (e.g. a zero-size container), degrade to
      // just the payload rather than breaking the drawer.
    }
  }

  const today = new Date();
  const minDate = new Date(today.getTime() - MAX_HISTORY_HOURS * 3600_000);
  fromInput.min = toInput.min = toDateInputValue(minDate);
  fromInput.max = toInput.max = toDateInputValue(today);

  hoursEl.innerHTML =
    HOURS_OPTIONS.map((o) => `<button type="button" data-hours="${o.hours}">${o.label}</button>`).join('') +
    '<button type="button" data-custom id="checkins-custom-btn">Custom &#9662;</button>';
  const customBtn = hoursEl.querySelector<HTMLButtonElement>('#checkins-custom-btn')!;

  function renderHoursActive(): void {
    hoursEl.querySelectorAll<HTMLButtonElement>('button[data-hours]').forEach((btn) => {
      const active = !custom && Number(btn.dataset.hours) === hours;
      btn.classList.toggle('active', active);
      btn.classList.toggle('accent', active);
    });
    customBtn.classList.toggle('active', !!custom);
    customBtn.classList.toggle('accent', !!custom);
    customBtn.innerHTML = custom ? escapeHtml(formatRangeLabel(custom.from, custom.to)) : 'Custom &#9662;';
  }
  renderHoursActive();

  hoursEl.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const presetBtn = target.closest<HTMLButtonElement>('button[data-hours]');
    if (presetBtn) {
      hours = Number(presetBtn.dataset.hours);
      custom = null;
      popoverEl.hidden = true;
      renderHoursActive();
      load();
      return;
    }
    if (target.closest('button[data-custom]')) {
      popoverEl.hidden = !popoverEl.hidden;
    }
  });

  applyBtn.addEventListener('click', () => {
    let from = fromInput.value;
    let to = toInput.value;
    if (!from || !to) return;
    if (from > to) [from, to] = [to, from];
    custom = { from, to };
    popoverEl.hidden = true;
    renderHoursActive();
    load();
  });

  rangeClearBtn.addEventListener('click', () => {
    custom = null;
    fromInput.value = '';
    toInput.value = '';
    popoverEl.hidden = true;
    renderHoursActive();
    load();
  });

  const onDocClick = (e: MouseEvent): void => {
    if (popoverEl.hidden) return;
    if (!(e.target as HTMLElement).closest('.log-range')) popoverEl.hidden = true;
  };
  document.addEventListener('click', onDocClick);

  searchInput.addEventListener('input', () => {
    searchClear.hidden = searchInput.value === '';
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
      query = searchInput.value.trim().toLowerCase();
      renderRows();
    }, 120);
  });
  searchClear.addEventListener('click', () => {
    searchInput.value = '';
    searchClear.hidden = true;
    query = '';
    renderRows();
    searchInput.focus();
  });

  backBtn.addEventListener('click', () => navigate('/people'));

  function showError(err: unknown): void {
    const message = err instanceof ApiError ? err.message : 'Something went wrong. Please try again.';
    errorBox.textContent = message;
    errorBox.hidden = false;
  }

  function clearError(): void {
    errorBox.hidden = true;
    errorBox.textContent = '';
  }

  function rowId(item: CheckinItem): string {
    return item.timestamp;
  }

  function searchHaystack(item: CheckinItem): string {
    return [
      `${item.lat.toFixed(5)}, ${item.lng.toFixed(5)}`,
      connectedSsid(item) ?? '—',
      new Date(item.timestamp).toLocaleString(),
      typeof item.accuracy === 'number' ? `±${Math.round(item.accuracy)} m${item.imprecise ? ' — imprecise' : ''}` : 'unknown',
      typeof item.battery === 'number' ? `${item.battery}%` : 'unknown',
    ]
      .join(' ')
      .toLowerCase();
  }

  function inCustomRange(item: CheckinItem): boolean {
    if (!custom) return true;
    const t = new Date(item.timestamp).getTime();
    const start = new Date(`${custom.from}T00:00:00`).getTime();
    // Next-day midnight via date arithmetic, not +24h — DST days are 23/25h.
    const endDate = new Date(`${custom.to}T00:00:00`);
    endDate.setDate(endDate.getDate() + 1);
    return t >= start && t < endDate.getTime();
  }

  function buildDrawer(item: CheckinItem): HTMLDivElement {
    const json = JSON.stringify(item, null, 2);
    const drawer = document.createElement('div');
    drawer.className = 'log-drawer';
    const coords = `${item.lat.toFixed(5)}, ${item.lng.toFixed(5)}`;
    drawer.innerHTML = `
      <div class="log-drawer-bar">
        <span class="log-drawer-label">Check-in detail</span>
        <div class="log-drawer-actions">
          <button type="button" class="log-drawer-btn" data-fullmap>&#8599; Open in full map</button>
          <button type="button" class="log-drawer-btn" data-copy>&#10697; Copy</button>
        </div>
      </div>
      <div class="log-drawer-body">
        <div class="log-map-wrap">
          <div class="log-map"></div>
          <div class="log-map-caption">${escapeHtml(coords)} &middot; ${escapeHtml(relativeTime(item.timestamp))}</div>
        </div>
        <pre>${highlightJson(json)}</pre>
      </div>
    `;
    drawer.querySelector<HTMLButtonElement>('button[data-fullmap]')!.addEventListener('click', () => {
      navigate(`/?lat=${item.lat}&lng=${item.lng}`);
    });
    const copyBtn = drawer.querySelector<HTMLButtonElement>('button[data-copy]')!;
    copyBtn.addEventListener('click', () => {
      navigator.clipboard
        ?.writeText(json)
        .then(() => {
          copyBtn.classList.add('copied');
          copyBtn.innerHTML = '&#10003; Copied';
          window.setTimeout(() => {
            if (disposed) return;
            copyBtn.classList.remove('copied');
            copyBtn.innerHTML = '&#10697; Copy';
          }, 1600);
        })
        .catch(() => {});
    });
    return drawer;
  }

  function renderRows(): void {
    destroyMiniMap();
    bodyEl.innerHTML = '';

    const filtered = allRows.filter((item) => inCustomRange(item) && (!query || searchHaystack(item).includes(query)));

    if (filtered.length === 0) {
      const message =
        allRows.length === 0
          ? 'No check-ins in this window.'
          : query
            ? `No check-ins match "${escapeHtml(searchInput.value.trim())}".`
            : 'No check-ins in this range.';
      bodyEl.innerHTML = `<div class="log-empty">${message}</div>`;
      return;
    }

    const table = document.createElement('div');
    table.className = 'log-table';
    table.innerHTML = `
      <div class="log-table-head">
        <span>Timestamp</span><span class="cell-lag">Lag</span><span>Location</span><span class="cell-acc">Accuracy</span><span>Battery</span><span class="cell-ssid">Wi-Fi</span>
      </div>
    `;

    const closeDrawer = (): void => {
      destroyMiniMap();
      table.querySelector('.log-drawer')?.remove();
      table.querySelectorAll('.log-row.open').forEach((r) => r.classList.remove('open'));
      openId = null;
    };

    // The still-open row (if any) mounts its map after the table is attached,
    // so Leaflet reads a real container size instead of zero.
    let pendingMount: (() => void) | undefined;

    filtered.forEach((item) => {
      const id = rowId(item);
      const lagSeconds = (new Date(item.received_at).getTime() - new Date(item.timestamp).getTime()) / 1000;

      const row = document.createElement('div');
      row.className = 'log-row';
      row.tabIndex = 0;
      row.setAttribute('role', 'button');

      const locLink = document.createElement('a');
      locLink.href = `/?lat=${item.lat}&lng=${item.lng}`;
      locLink.className = 'mono-accent';
      locLink.textContent = `${item.lat.toFixed(5)}, ${item.lng.toFixed(5)}`;
      locLink.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/?lat=${item.lat}&lng=${item.lng}`);
      });

      row.innerHTML = `
        <span class="cell-time"><span class="abs">${new Date(item.timestamp).toLocaleString()}</span><span class="rel">(${relativeTime(item.timestamp)})</span></span>
        <span class="mono-dim cell-lag">${humanDuration(lagSeconds)}</span>
        <span class="cell-loc"></span>
        <span class="mono-dim cell-acc${item.imprecise ? ' imprecise' : ''}">${typeof item.accuracy === 'number' ? `±${Math.round(item.accuracy)} m${item.imprecise ? ' — imprecise' : ''}` : 'unknown'}</span>
        <span style="font-family:'IBM Plex Mono';color:${batteryColor(item.battery)}">${typeof item.battery === 'number' ? `${item.battery}%` : 'unknown'}</span>
        <span class="mono-ssid cell-ssid">${escapeHtml(connectedSsid(item) ?? '—')}</span>
      `;
      row.querySelector('.cell-loc')!.appendChild(locLink);

      const toggleRow = (): void => {
        const wasOpen = openId === id;
        closeDrawer();
        if (wasOpen) return;
        openId = id;
        row.classList.add('open');
        const drawer = buildDrawer(item);
        row.after(drawer);
        mountMiniMap(drawer, item);
      };
      row.addEventListener('click', toggleRow);
      row.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleRow();
        }
      });

      table.appendChild(row);
      if (id === openId) {
        row.classList.add('open');
        const drawer = buildDrawer(item);
        table.appendChild(drawer);
        pendingMount = () => mountMiniMap(drawer, item);
      }
    });

    bodyEl.appendChild(table);
    pendingMount?.();
  }

  async function load(): Promise<void> {
    // Guards against out-of-order responses: a slow 7d fetch must not
    // overwrite the table after a quick 6h fetch has already rendered.
    const seq = ++loadSeq;
    clearError();
    bodyEl.innerHTML = 'Loading&hellip;';
    // The API only takes an hours-back window, so a custom range fetches
    // enough hours to reach its start date and trims client-side.
    let fetchHours = hours;
    if (custom) {
      const start = new Date(`${custom.from}T00:00:00`).getTime();
      fetchHours = Math.min(MAX_HISTORY_HOURS, Math.max(1, Math.ceil((Date.now() - start) / 3600_000)));
    }
    try {
      const [res, overview] = await Promise.all([api.checkins(deviceId, fetchHours), api.overview()]);
      if (disposed || seq !== loadSeq) return;
      const device = overview.users.flatMap((u) => u.devices).find((d) => d.device_id === deviceId);
      const user = overview.users.find((u) => u.devices.some((d) => d.device_id === deviceId));
      titleEl.textContent = 'Check-in log';
      subtitleEl.textContent = device ? `${user ? user.display_name + ' · ' : ''}${device.device_name}` : deviceId;
      allRows = [...res.checkins].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      renderRows();
    } catch (err) {
      if (disposed || seq !== loadSeq) return;
      showError(err);
      bodyEl.innerHTML = '';
    }
  }

  load();

  return () => {
    disposed = true;
    window.clearTimeout(searchTimer);
    document.removeEventListener('click', onDocClick);
    destroyMiniMap();
  };
}
